import { ContextMenu, ContextMenuDeleteItem, ContextMenuItem, Id, useHasPermission } from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../../shared/types/permission";
import { EventDetailResource } from "../../shared/types";
import { useDeleteEvent } from "../useDeleteEvent";

interface Props {
	event: EventDetailResource;
}

export const EventContextMenu: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const deleteEvent = useDeleteEvent(false);

	const canCreate = useHasPermission<PermissionName>(["event.create"]);
	const canUpdate = useHasPermission<PermissionName>(["event.update"]);
	const canDelete = useHasPermission<PermissionName>(["event.delete"]);

	const handleDelete = useCallback(
		async (eventId: Id) => {
			if (await deleteEvent(eventId)) {
				navigate(-1);
			}
		},
		[deleteEvent, navigate],
	);

	if (!canUpdate && !canDelete && !event.hasProjectMemberPermissions && !event.hasProjectManagerPermissions) {
		return null;
	}

	return (
		<ContextMenu hideIconOnOnlyShortcuts={true} displayShortcutsAs="icon">
			{(canUpdate || event.hasProjectMemberPermissions) && (
				<ContextMenuItem
					action={() => navigate(`/events/active/${event.id}/update/base-data`)}
					title={t("Edit event")}
					label={t("Edit event")}
					icon="edit"
					shortcut="primary"
				/>
			)}
			{canCreate && (
				<ContextMenuItem
					action={() => navigate(`/events/create/from-event/${event.id}/base-data`)}
					title={t("Duplicate event")}
					label={t("Duplicate event")}
					icon="copy"
				/>
			)}
			{(canUpdate || event.hasProjectManagerPermissions) && (
				<ContextMenuItem
					action={() => navigate(`/events/active/${event.id}/update-event-type/base-data`)}
					title={t("Change event type")}
					label={t("Change event type")}
					iconSrc="/change_event_type.svg"
				/>
			)}
			{(canDelete || event.hasProjectManagerPermissions) && (
				<ContextMenuDeleteItem
					action={() => handleDelete(event.id)}
					title={t("Archive event")}
					label={t("Archive event")}
					heading={t("Archive event")}
					labels={{ close: t("Close"), confirm: t("Confirm") }}
				>
					{t("Are you sure you want to archive this event?")}
				</ContextMenuDeleteItem>
			)}
		</ContextMenu>
	);
};
